import { Signal } from "@suse-o11y/signal";
import React from "react";
import { useMeasure } from "react-use";

import { Button } from "~/components/button";
import { useGridLayoutContext } from "~/components/grid-layout/use-grid-layout-context";
import { IconQuestionCircleOutlined } from "~/components/icon";
import { Idle } from "~/components/idle";
import { SkeletonBox } from "~/components/skeleton/box";
import { SkeletonText } from "~/components/skeleton/text";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { Truncated } from "~/components/truncated";
import type { MetricQuery } from "~/models/metric-query";
import { DASHBOARD_WIDGET_QUERY_NAMESPACE } from "~/pages/dashboards/widget/query-namespace";
import { cx } from "~/tools/class-variance";
import { drop } from "~/tools/drop";
import type { TemplateVariableDefinitions } from "~/tools/template-variables/definitions";
import { useTemplateVariables } from "~/tools/template-variables/use-template-variables";

import type { SpecificRange, TimeRange } from "../../app/state/view/timeline/TimeRange";
import {
	DashboardChartPluginSpec,
	type DashboardPluginSpec,
	type DashboardWidgetSpec,
} from "../../common/dashboard-spec";
import { Widget, WidgetBody, WidgetHeader } from "../_widget";
import { useDashboardWidgetState } from "../dashboard/use-widget-state";
import { EmptyChart } from "../react/chart/chart-states/EmptyChart";
import { ErrorChart } from "../react/chart/chart-states/ErrorChart";
import { useExplorationDrawerState } from "../react/drawer/use-exploration-drawer-state";
import { LoadingSpinner } from "../react/loading-spinner/LoadingSpinner";
import { TimeSeriesChartState } from "../TimeSeriesChart/state";
import { Chart } from "../widget-form";
import { useQueryTimeSeries } from "../widget-form/use-query-time-series";

import { DashboardWidgetActions, type DashboardWidgetActionsProps } from "./_dashboard-widget-actions";
import { DashboardWidgetLinks } from "./_dashboard-widget-links";

const LazyDashboardMarkdownWidget = React.lazy(async () => {
	const { DashboardMarkdownWidget } = await import("./_dashboard-markdown-widget");
	return { default: DashboardMarkdownWidget };
});

export interface DashboardWidgetProps extends Omit<DashboardWidgetActionsProps, "onEnlarge"> {
	className?: string;
	style?: React.CSSProperties;
	identity: string;
	spec: DashboardWidgetSpec;
	timeRange: TimeRange;
	onTimeRangeChange?: (timeRange: SpecificRange) => void;
	onEnlarge?: (plugin: Exclude<DashboardPluginSpec, { kind: "Markdown" }>) => void;
	children?: React.ReactNode;
}

export const DashboardWidget = React.forwardRef<HTMLDivElement, DashboardWidgetProps>(
	(
		{ className, identity, spec, timeRange, onTimeRangeChange, onDelete, onEdit, onEnlarge, children, ...props },
		ref,
	) => {
		const [markdown, metrics] = spec.plugin.kind === "Markdown" ? [spec.plugin, null] : [null, spec.plugin];
		const variables = useTemplateVariables();

		const gridLayoutContext = useGridLayoutContext();
		const primaryLink = spec.plugin.links.at(0);

		const interpolatedName = spec.name.interpolate(variables.values);
		const interpolatedDescription = spec.description.interpolate(variables.values);

		return (
			<Widget card ref={ref} {...props} className={cx(className, "group/dashboard-widget")}>
				<WidgetHeader
					className={cx(
						"grid-cols-[1fr_auto] justify-items-stretch gap-x-6",
						onEdit && "cursor-grab active:cursor-grabbing",
						gridLayoutContext?.dragHandleClassName,
					)}
					description={
						primaryLink != null && (
							<DashboardWidgetLinks otherLinks={drop(spec.plugin.links, 1)} primaryLink={primaryLink} />
						)
					}
					title={
						<SkeletonText className="flex flex-row items-center" randomize when={interpolatedName}>
							{(name) => <Truncated>{name}</Truncated>}

							{interpolatedDescription && (
								<Tooltip>
									<TooltipTrigger>
										<Button className={cx("ml-1", gridLayoutContext?.dragCancelClassName)} icon size="xs">
											<IconQuestionCircleOutlined className="text-base/none text-grey-600" />
										</Button>
									</TooltipTrigger>

									<TooltipContent>{interpolatedDescription}</TooltipContent>
								</Tooltip>
							)}

							<LoadingSpinner
								// show the spinner with a delay to avoid flickering when loading is very fast
								className="ml-3 items-start opacity-0 transition-opacity duration-200 group-has-data-[state=fetching]/dashboard-widget:opacity-100 group-has-data-[state=fetching]/dashboard-widget:delay-100"
								size="small"
							/>
						</SkeletonText>
					}
				>
					<DashboardWidgetActions
						onDelete={onDelete}
						onEdit={onEdit}
						onEnlarge={onEnlarge == null || metrics == null ? undefined : () => onEnlarge(metrics)}
					/>
				</WidgetHeader>

				<WidgetBody className="min-h-0 flex-1">
					<React.Suspense fallback={<SkeletonBox className="size-full" />}>
						{markdown ? (
							<LazyDashboardMarkdownWidget spec={markdown.spec} />
						) : (
							<MetricsWidget
								identity={identity}
								onTimeRangeChange={onTimeRangeChange}
								plugin={metrics}
								timeRange={timeRange}
								title={interpolatedName}
								variables={variables}
							/>
						)}
					</React.Suspense>
				</WidgetBody>

				{/* The dashboard container mounts a resize handle as a child */}
				{children}
			</Widget>
		);
	},
);

interface MetricsWidgetProps {
	identity: string;
	timeRange: TimeRange;
	title: undefined | string;
	variables: TemplateVariableDefinitions;
	plugin: Exclude<DashboardPluginSpec, { kind: "Markdown" }>;
	onTimeRangeChange: undefined | ((timeRange: SpecificRange) => void);
}

const MetricsWidget: React.FC<MetricsWidgetProps> = ({
	identity,
	timeRange,
	title,
	variables,
	plugin,
	onTimeRangeChange,
}) => {
	const state = useDashboardWidgetState(() => new TimeSeriesChartState());
	const [measureRef, { width }] = useMeasure<HTMLDivElement>();
	const explorationDrawer = useExplorationDrawerState();

	const chart = React.useMemo(() => DashboardChartPluginSpec.toChartSpec(plugin), [plugin]);

	const metricQueries = React.useMemo(
		() =>
			plugin.queries.map(
				({ query, alias, minStep }) =>
					({
						promql: query.interpolate(variables.values) ?? "",
						label: alias?.addVariables(variables.values),
						step: minStep?.interpolate(variables.values),
					}) satisfies MetricQuery,
			),
		[plugin.queries, variables.values],
	);

	const onShowAll = () => {
		explorationDrawer.openMetrics({
			metricQueries,
			timestamp: undefined,
			timeRange: Signal(timeRange),
			title,
			chart,
		});
	};

	const result = useQueryTimeSeries({
		batch: identity,
		timeRange,
		queries: metricQueries,
		width: plugin.kind === "GaugeChart" || plugin.kind === "StatChart" ? undefined : width,
		namespace: DASHBOARD_WIDGET_QUERY_NAMESPACE,
		retry: false,
	});

	return result.match({
		idle: () => (
			<div className="size-full" ref={measureRef}>
				<EmptyChart />
			</div>
		),

		loading: () => <SkeletonBox className="size-full" ref={measureRef} />,

		failure: ({ error }) => <ErrorChart error={error} ref={measureRef} />,

		success: ({ value: { isFetching, timeSeries } }) => (
			<div className="size-full" data-state={isFetching ? "fetching" : "success"} ref={measureRef}>
				{timeSeries.length === 0 ? (
					<EmptyChart />
				) : (
					<Idle fallback={<SkeletonBox className="size-full" />}>
						<Chart
							fallback={<SkeletonBox className="size-full" />}
							onShowAll={onShowAll}
							onTimeRangeChange={onTimeRangeChange}
							spec={chart}
							state={state}
							timeRange={timeRange}
							timeSeries={timeSeries}
						/>
					</Idle>
				)}
			</div>
		),
	});
};
